import $axios from "../../api";

/**
 * DashboardIT store — fetch parallel ke 3 endpoint terpisah (header/queue/summary)
 * supaya UI bisa render progressif. Sebelumnya satu endpoint `data/list` cold-fetch
 * ~95-100 detik karena 1 aggregate + 32 list query — semua nge-block render.
 *
 * Loading flags terpisah per section supaya tiap panel bisa tampil skeleton
 * sendiri sampai data masing-masing siap.
 */

const state = () => ({
  data: {},
  modalData: [],

  // Per-section loading state — true = belum return dari server
  isLoadingHeader: false,
  isLoadingQueue: false,
  isLoadingSummary: false,
});

const mutations = {
  SET_HEADER(state, payload) {
    // payload: { date_time, header: [4 cards] }
    state.data = { ...state.data, ...payload };
  },
  SET_QUEUE(state, payload) {
    // payload: { sync_queue_toko_to_unionweb, sync_queue_unionweb_to_union }
    state.data = { ...state.data, ...payload };
  },
  SET_SUMMARY(state, payload) {
    // payload: { sync_table_toko_to_unionweb, sync_table_unionweb_to_union }
    state.data = { ...state.data, ...payload };
  },
  SET_MODAL_DATA(state, payload) {
    state.modalData = payload;
  },
  SET_RESYNC_STATE(state, { index, item }) {
    state.modalData.splice(index, 1, item);
  },
  SET_LOADING_HEADER(state, payload) {
    state.isLoadingHeader = payload;
  },
  SET_LOADING_QUEUE(state, payload) {
    state.isLoadingQueue = payload;
  },
  SET_LOADING_SUMMARY(state, payload) {
    state.isLoadingSummary = payload;
  },
};

const actions = {
  /**
   * Fetch header counts (4 angka). Ringan: 1 query saja, ~5 detik cold.
   */
  getHeader({ commit, dispatch }) {
    commit("SET_LOADING_HEADER", true);
    return $axios.get("dashboardsync/data/header")
      .then((response) => {
        commit("SET_HEADER", response.data.data || {});
        return response;
      })
      .catch((error) => {
        dispatch("Main/globalSweetAlert", error.response, { root: true });
        throw error;
      })
      .finally(() => {
        commit("SET_LOADING_HEADER", false);
      });
  },

  /**
   * Fetch queue lists (2 tabel limit 20). Medium: 2 query, ~5-10 detik cold.
   */
  getQueue({ commit, dispatch }) {
    commit("SET_LOADING_QUEUE", true);
    return $axios.get("dashboardsync/data/queue")
      .then((response) => {
        commit("SET_QUEUE", response.data.data || {});
        return response;
      })
      .catch((error) => {
        dispatch("Main/globalSweetAlert", error.response, { root: true });
        throw error;
      })
      .finally(() => {
        commit("SET_LOADING_QUEUE", false);
      });
  },

  /**
   * Fetch per-table stats (right view). Heaviest: 1 aggregate + 32 list query,
   * cold ~80-100 detik. Cached subsequent <1ms.
   */
  getSummary({ commit, dispatch }) {
    commit("SET_LOADING_SUMMARY", true);
    return $axios.get("dashboardsync/data/summary")
      .then((response) => {
        commit("SET_SUMMARY", response.data.data || {});
        return response;
      })
      .catch((error) => {
        dispatch("Main/globalSweetAlert", error.response, { root: true });
        throw error;
      })
      .finally(() => {
        commit("SET_LOADING_SUMMARY", false);
      });
  },

  /**
   * Fetch semua 3 section paralel via Promise.allSettled.
   * Tetap return Promise supaya caller bisa await — tapi UI sudah render progresif
   * via per-section loading flag sebelum await resolve.
   */
  getDashboardITData({ dispatch }) {
    return Promise.allSettled([
      dispatch("getHeader"),
      dispatch("getQueue"),
      dispatch("getSummary"),
    ]);
  },

  resyncExternalData({ commit, dispatch, state }, { item, index }) {
    commit("SET_RESYNC_STATE", { index, item: { ...item, isResyncing: true } });
    return new Promise((resolve, reject) => {
      const endpoint = `dashboardsync/resync/${item.endpoint_resync}`;

      $axios.post(endpoint, { sync_master_id: [item.id] })
        .then(async (response) => {
          // Refresh 3 section parallel — header & queue rebuild quick,
          // summary slower karena re-aggregate per-table stats.
          await dispatch("getDashboardITData");
          dispatch("Main/globalCustomSweetAlert", {
            message: `${item.key_value} Resync successful`,
            position: "top-end",
            timer: 2000,
          }, { root: true });
          resolve(response);
        })
        .catch((error) => {
          dispatch("Main/globalSweetAlert", {
            message: `Error resyncing data: ${error.message}`,
            icon: "error",
          }, { root: true });
          reject(error);
        })
        .finally(() => {
          commit("SET_RESYNC_STATE", { index, item: { ...item, isResyncing: false } });

          const tableKey = item.table_name;
          let updatedList;

          if (item.endpoint_resync === "web") {
            updatedList = state.data.sync_table_toko_to_unionweb?.[tableKey]?.list || state.modalData;
          } else {
            updatedList = state.data.sync_table_unionweb_to_union?.[tableKey]?.list || state.modalData;
          }

          commit("SET_MODAL_DATA", updatedList);
        });
    });
  },
};

const getters = {
  // Computed flag: dashboard "loading" overall hanya selama 3 endpoint belum return.
  // Kalau salah satu sudah return, UI render section itu — progressive paint.
  isLoading: (state) =>
    state.isLoadingHeader && state.isLoadingQueue && state.isLoadingSummary,
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
